import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Grid2, useTheme, Button } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { cancelAppointment, getAppointmentById } from '../firebase/firebase-delegate';
import LoadingSpinner from '../components/global/LoadingSpinner';
import { formatDate, formatTimeRange } from '../time/timeUtils';
import SuccessOrErrorDialog from '../components/global/SuccessOrErrorDialog';
import { Appointment } from '../types';

const CustomerAppointmentPage: React.FC = () => {
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const appointmentId = searchParams.get('id');
    const navigate = useNavigate();

    const appointmentQuery = useQuery<Appointment>({
        queryKey: ['appointment', appointmentId],
        queryFn: () => getAppointmentById(appointmentId!),
        enabled: !!appointmentId,
        retry: false,
    });

    const cancelMutation = useMutation({
        mutationFn: async () => {
            if (appointmentQuery.data) {
                await cancelAppointment(appointmentQuery.data, 'customer');
            }
        }
    });

    useEffect(() => {
        if (appointmentQuery.isSuccess && appointmentQuery.data.status === 'CANCELLED') {
            navigate('/404');
        }
    }, [appointmentQuery.data?.status, appointmentQuery.isSuccess, navigate]);

    if (!appointmentId || appointmentQuery.isError) {
        navigate('/404');
        return null;
    }

    if (appointmentQuery.isLoading) return <LoadingSpinner />;
    if (appointmentQuery.isError || !appointmentQuery.data) {
        return <Typography variant="h6" align="center" mt={4}>Appointment not found.</Typography>;
    }

    const appointment = appointmentQuery.data;
    const isRefunded = appointment?.paymentInfo?.refunded === true;
    const showPaidBreakdown = Boolean(appointment?.paid) || isRefunded;
    const subtotal = showPaidBreakdown && typeof appointment?.paymentInfo?.serviceAmountCents === 'number'
        ? appointment.paymentInfo.serviceAmountCents / 100
        : appointment?.billingAmount ?? 0;
    const tip = showPaidBreakdown && typeof appointment?.paymentInfo?.tipAmountCents === 'number'
        ? appointment.paymentInfo.tipAmountCents / 100
        : 0;
    const total = showPaidBreakdown && typeof appointment?.paymentInfo?.amountCents === 'number'
        ? appointment.paymentInfo.amountCents / 100
        : subtotal + tip;
    const fullName = `${appointment.customerFirstName} ${appointment.customerLastName}`;
    const timeRange = formatTimeRange(appointment.appointmentTime, appointment.appointmentDuration);

    return (
        <Box display="flex" justifyContent="center" mt={6} px={2}>
            <Card
                sx={{
                    width: '100%',
                    maxWidth: 600,
                    borderRadius: 2,
                    padding: 2,
                    boxShadow: theme.shadows[3],
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <CardContent>
                    <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
                        Appointment Details
                    </Typography>
                    <Typography variant="subtitle2" align="center" gutterBottom>
                        We look forward to seeing you!
                    </Typography>
                    <hr
                        style={{
                            border: 'none',
                            height: '2px',
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            boxShadow: '0 1px 5px rgba(0, 0, 0, 0.3)',
                            marginTop: '20px',
                            marginBottom: '20px',
                        }}
                    />
                    <Grid2 container spacing={2}>

                        <Grid2 size={12}>
                            <Typography variant="h6">Customer: {fullName}</Typography>
                        </Grid2>
                        <Grid2 size={12}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Scheduled Services:
                            </Typography>
                            <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                                {appointment.servicesList.map((service, index) => (
                                    <li key={index} >
                                        <Typography variant="body1">
                                            <strong>{service.serviceTitle}</strong> — ${service.serviceAmount.toFixed(2)} ({service.serviceTime} mins)
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                        </Grid2>
                        <Grid2 size={12}>
                            <Typography variant="body1">Appointment ID: {appointment.id.slice(0, 6)}</Typography>
                        </Grid2>
                        <Grid2 size={12}>
                            <Typography variant="body1">Phone: {appointment.customerPhoneNumber}</Typography>
                        </Grid2>
                        <Grid2 size={12}>
                            <Typography variant="body1">Email: {appointment.customerEmail}</Typography>
                        </Grid2>
                        <Grid2 size={12}>
                            <Typography variant="body1">Date: {formatDate(appointment.appointmentDate)}</Typography>
                        </Grid2>
                        <Grid2 size={12}>
                            <Typography variant="body1">Time: {timeRange}</Typography>
                        </Grid2>
                        {!showPaidBreakdown ? (
                            <Grid2 size={12}>
                                <Typography variant="h6">Billing Amount: ${appointment.billingAmount.toFixed(2)}</Typography>
                            </Grid2>
                        ) : (
                            <>
                                <Grid2 size={12}>
                                    <Typography variant="h6">Subtotal: ${subtotal.toFixed(2)}</Typography>
                                </Grid2>
                                {tip > 0 && (
                                    <Grid2 size={12}>
                                        <Typography variant="h6">Tip: ${tip.toFixed(2)}</Typography>
                                    </Grid2>
                                )}
                                <Grid2 size={12}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Total: ${total.toFixed(2)}</Typography>
                                </Grid2>
                            </>
                        )}
                        <Grid2 size={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => {
                                        if (!cancelMutation.isPending && !cancelMutation.isSuccess) {
                                            cancelMutation.mutate();
                                        }
                                    }}
                                    disabled={cancelMutation.isPending}
                                    sx={{ width: { xs: '100%', sm: '50%' } }}
                                >
                                    Cancel Appointment
                                </Button>

                            </Box>
                        </Grid2>
                    </Grid2>
                </CardContent>
            </Card>

            <SuccessOrErrorDialog
                open={cancelMutation.isSuccess || cancelMutation.isError}
                onClose={() => {
                    if (cancelMutation.isSuccess) {
                        navigate('/');
                    }
                }}
                success={cancelMutation.isSuccess}
                successTitle="Appointment Canceled"
                successMessage="Your appointment has been canceled successfully."
                errorTitle="Error"
                errorMessage="There was a problem canceling your appointment. Please try again."
            />
        </Box>
    );
};

export default CustomerAppointmentPage;
